import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, X, Video, Bell, Mic, Home, Search } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import StreamsGlyph from './icons/StreamsGlyph';
import RoomsGlyph from './icons/RoomsGlyph';
import CirclesGlyph from './icons/CirclesGlyph';

// PNG-masked Streams icon (inherits currentColor)
const CustomPlayIcon: React.FC<{ className?: string }> = ({ className = "w-6 h-6" }) => (
  <StreamsGlyph className={className} />
);

export const BottomNavigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showQuickActions, setShowQuickActions] = useState(false);
  const { isFemale, secondaryColor } = useTheme();

  // Mock user profile picture - in real app this would come from user context
  const userProfilePicture = "https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=400";

  const tabs = [
    { id: 'home', label: 'Home', icon: Home, path: '/home' },
    { id: 'search', label: 'Search', icon: Search, path: '/search' },
    { id: 'streams', label: 'Streams', icon: CustomPlayIcon, path: '/streams' },
    { id: 'circles', label: 'Circles', icon: CirclesGlyph, path: '/circles' },
    { id: 'rooms', label: 'Rooms', icon: RoomsGlyph, path: '/rooms' },
    { id: 'profile', label: 'Profile', path: '/profile' },
  ];

  const quickActions = [
    { id: 'create', label: 'Create Content', icon: Plus, path: '/create', color: `bg-[${secondaryColor}]` },
    { id: 'chat', label: 'Messages', icon: Video, path: '/chat', color: 'bg-green-500' },
    { id: 'live', label: 'Live Stream', icon: Video, path: '/live-streaming', color: 'bg-red-500' },
    { id: 'notifications', label: 'Notifications', icon: Bell, path: '/notifications', color: 'bg-blue-500' },
    { id: 'voice', label: 'Voice & Audio', icon: Mic, path: '/voice-audio', color: 'bg-purple-500' },
    { id: 'recommendations', label: 'Discover', icon: Plus, path: '/recommendations', color: 'bg-indigo-500' },
    { id: 'monetization', label: 'Earnings', icon: Plus, path: '/monetization', color: 'bg-yellow-500' },
  ];

  const handleTabClick = (path: string) => { 
    if (location.pathname === path) {
      // If already on this page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(path); 
    }
    setShowQuickActions(false); 
  };
  const handleQuickAction = (path: string) => { navigate(path); setShowQuickActions(false); };
  const activeTabColor = isFemale ? 'text-[#EEBECE] bg-[#EEBECE]/10' : 'text-[#B7CBDF] bg-[#B7CBDF]/10';
  const quickActionsBg = isFemale ? 'bg-[#EEBECE]' : 'bg-[#B7CBDF]';
  
  // Check if we're on Streams for black navbar
  const isOnStreams = location.pathname === '/streams';

  return (
    <>
      {showQuickActions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowQuickActions(false)}>
          <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-white rounded-2xl p-4 shadow-xl">
            <div className="grid grid-cols-3 gap-4">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <button key={action.id} onClick={() => handleQuickAction(action.path)} className="flex flex-col items-center space-y-2 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                    <div className={`w-12 h-12 rounded-full ${action.color} flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-xs text-gray-600 text-center">{action.label}</span>
                  </button>
                );
              })}
            </div>
            <button onClick={() => setShowQuickActions(false)} className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
      )}
      <nav 
        className="fixed bottom-0 left-0 right-0 z-30 max-w-md mx-auto transition-colors duration-300"
        style={{ backgroundColor: isOnStreams ? '#000000' : '#FFFFFF' }}
      >
        <div className="flex items-center justify-around px-2" style={{ height: '49px' }}>
          {tabs.map((tab) => {
            const isActive = location.pathname === tab.path;
            
            // Special handling for profile tab
            if (tab.id === 'profile') {
              return (
                <button 
                  key={tab.id} 
                  onClick={() => handleTabClick(tab.path)} 
                  className="flex items-center justify-center flex-1 h-full"
                  style={{ minWidth: '40px' }}
                >
                  <div className={`w-6 h-6 rounded-full overflow-hidden transition-all ${
                    isActive 
                      ? (isOnStreams ? 'ring-[1.5px] ring-white' : 'ring-[1.5px] ring-black') 
                      : ''
                  }`}>
                    <img
                      src={userProfilePicture}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </button>
              );
            }
            
            const IconComponent = tab.icon as any;
            
            // Color logic based on Streams screen
            let iconColor;
            if (isOnStreams) {
              iconColor = isActive ? '#FFFFFF' : '#A3A3A3'; // White active, light gray inactive on black nav
            } else {
              iconColor = isActive ? '#000000' : '#737373'; // Black active, gray inactive on white nav
            }
            
            return (
              <button 
                key={tab.id} 
                onClick={() => handleTabClick(tab.path)} 
                className="flex items-center justify-center flex-1 h-full transition-colors"
                style={{ 
                  minWidth: '40px',
                  color: iconColor
                }}
              >
                <IconComponent 
                  className={`${
                    tab.id === 'rooms' 
                      ? 'w-7 h-7' // Rooms icon
                      : 'w-6 h-6' // Standard size for all icons
                  } transition-all`}
                  strokeWidth={isActive ? 2.5 : 2}
                  style={{ 
                    fill: isActive && (tab.id === 'home' || tab.id === 'streams') ? 'currentColor' : 'none'
                  }}
                />
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};